import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ClipboardIcon from '@atlaskit/icon/core/clipboard';
import CrossIcon from '@atlaskit/icon/core/cross';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Tooltip from '@atlaskit/tooltip';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { AudioFeatureType } from '../../../api/cc-smarts/models/AudioFeature';
import { TrackType } from '../../../api/cc-smarts/models/TrackType';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioShareLinkCopy } from '../../hooks/useAudioShareLinkCopy';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';

import type { PlaylistItemData } from './PlaylistItem';

const messages = defineMessages({
	moreOptions: {
		id: 'ai-audio.playlist.more-options',
		defaultMessage: 'More options',
		description: 'Tooltip and label for the more options button on a playlist item',
	},
	moveUp: {
		id: 'ai-audio.playlist.move-up',
		defaultMessage: 'Move up',
		description: 'Menu item to move a playlist item up in the list',
	},
	moveDown: {
		id: 'ai-audio.playlist.move-down',
		defaultMessage: 'Move down',
		description: 'Menu item to move a playlist item down in the list',
	},
	copyLink: {
		id: 'ai-audio.playlist.copy-link',
		defaultMessage: 'Copy link to audio',
		description: 'Menu item to copy the link to the audio',
	},
	removeFromPlaylist: {
		id: 'ai-audio.playlist.remove-from-playlist',
		defaultMessage: 'Remove from playlist',
		description: 'Menu item to remove an item from the playlist',
	},
});

type PlaylistItemContextualMenuProps = {
	onMenuOpenChange?: (isOpen: boolean) => void;
	isFirstItem?: boolean;
	isLastItem?: boolean;
	data: PlaylistItemData;
	currentIndex: number;
	allItems: PlaylistItemData[];
};

export const PlaylistItemContextualMenu = ({
	onMenuOpenChange,
	isFirstItem,
	isLastItem,
	data,
	currentIndex,
	allItems,
}: PlaylistItemContextualMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { cloudId } = useAudioPlayerStore();
	const { removeTrack, reorderTrack } = useContextMenuActions(cloudId);
	const { copyAudioShareLinkToClipboard } = useAudioShareLinkCopy();
	const { mainContent, supportingContent, trackType } = data;

	const { sendTrackEvent, sendUIEvent } = usePlatformInstrumentation();

	const handleOpenChange = (args: { isOpen: boolean }) => {
		onMenuOpenChange?.(args.isOpen);
		if (args.isOpen) {
			sendUIEvent({
				eventType: 'ui',
				action: 'opened',
				actionSubject: 'menu',
				actionSubjectId: 'playlist',
				source: 'playlist',
				attributes: {
					contentType: data.contentType,
					trackType,
					itemPosition: currentIndex,
				},
			});
		}
	};

	const handleMoveUp = async () => {
		if (isFirstItem || currentIndex <= 0) {
			return;
		}

		const targetTrack = {
			mainSource: mainContent.ari,
			supportingSources: supportingContent.map((content) => content.ari),
			trackType,
		};

		const newReferenceIndex = currentIndex - 2;
		const referenceItem = newReferenceIndex >= 0 ? allItems[newReferenceIndex] : null;
		const referenceTrack = referenceItem
			? {
					mainSource: referenceItem.mainContent.ari,
					supportingSources: referenceItem.supportingContent.map((content) => content.ari),
					trackType: referenceItem.trackType,
				}
			: undefined; // undefined means move to beginning

		await reorderTrack(targetTrack, referenceTrack, data.title);
		sendUIEvent({
			eventType: 'ui',
			action: 'reordered',
			actionSubject: 'playlistItem',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				contentType: data.contentType,
				trackType,
				oldPosition: currentIndex,
				newPosition: currentIndex - 1,
				totalPlaylistSize: allItems?.length,
			},
		});
	};

	const handleMoveDown = async () => {
		if (isLastItem || currentIndex >= allItems.length - 1) {
			return;
		}

		const targetTrack = {
			mainSource: mainContent.ari,
			supportingSources: supportingContent.map((content) => content.ari),
			trackType,
		};

		// When moving down, we want to place our item after the item that's currently below us
		const referenceItem = allItems[currentIndex + 1];
		const referenceTrack = {
			mainSource: referenceItem.mainContent.ari,
			supportingSources: referenceItem.supportingContent.map((content) => content.ari),
			trackType: referenceItem.trackType,
		};

		await reorderTrack(targetTrack, referenceTrack, data.title);
		sendUIEvent({
			eventType: 'ui',
			action: 'reordered',
			actionSubject: 'playlistItem',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				contentType: data.contentType,
				trackType,
				oldPosition: currentIndex,
				newPosition: currentIndex + 1,
				totalPlaylistSize: allItems?.length,
			},
		});
	};

	const handleCopyLink = () => {
		copyAudioShareLinkToClipboard(
			data.mainContent.link,
			data.trackType === TrackType.BRIEFING
				? AudioFeatureType.BRIEFING
				: AudioFeatureType.NARRATION,
			data.supportingContent,
		);
		sendUIEvent({
			eventType: 'ui',
			action: 'copied',
			actionSubject: 'playlistItem',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				contentType: data.contentType,
				trackType,
				itemPosition: currentIndex,
			},
		});
	};
	const handleRemove = async () => {
		const mainSourceAri = mainContent.ari;
		const supportingSourcesAris = supportingContent.map((content) => content.ari);
		await removeTrack(mainSourceAri, supportingSourcesAris, trackType, data.title)
			.catch((error) => {
				sendTrackEvent({
					eventType: 'track',
					action: 'error',
					actionSubject: 'playlistItemRemove',
					actionSubjectId: 'playlist',
					source: 'playlist',
					attributes: {
						error,
						contentType: data.contentType,
						trackType,
						itemPosition: currentIndex,
						totalPlaylistSize: allItems?.length,
					},
				});
			})
			.then(() => {
				sendUIEvent({
					eventType: 'ui',
					action: 'removed',
					actionSubject: 'playlistItem',
					actionSubjectId: 'playlist',
					source: 'playlist',
					attributes: {
						contentType: data.contentType,
						trackType,
						itemPosition: currentIndex,
						totalPlaylistSize: allItems?.length,
					},
				});
			});
	};

	return (
		<Tooltip content={formatMessage(messages.moreOptions)}>
			<DropdownMenu
				onOpenChange={handleOpenChange}
				trigger={({ triggerRef, ...triggerProps }) => (
					<IconButton
						{...triggerProps}
						ref={triggerRef}
						icon={MoreIcon}
						label={formatMessage(messages.moreOptions)}
						appearance="subtle"
						shape="circle"
					/>
				)}
				shouldRenderToParent={true}
			>
				<DropdownItemGroup>
					<DropdownItem
						onClick={handleMoveUp}
						elemBefore={<ArrowUpIcon label="" />}
						isDisabled={isFirstItem}
					>
						{formatMessage(messages.moveUp)}
					</DropdownItem>
					<DropdownItem
						onClick={handleMoveDown}
						elemBefore={<ArrowDownIcon label="" />}
						isDisabled={isLastItem}
					>
						{formatMessage(messages.moveDown)}
					</DropdownItem>
					<DropdownItem onClick={handleCopyLink} elemBefore={<ClipboardIcon label="" />}>
						{formatMessage(messages.copyLink)}
					</DropdownItem>
					{/* TODO: Update the remove item icon once its available */}
					<DropdownItem onClick={handleRemove} elemBefore={<CrossIcon label="" />}>
						{formatMessage(messages.removeFromPlaylist)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</Tooltip>
	);
};
